import type { Portfolio } from '@/types/portfolios';
import type { CampaignAdProduct, SpCampaignNormalized } from '@/types/spCampaigns';
import type { UnifiedKeywordRow } from '@/api/unifiedKeywords';

export const PORTFOLIO_NONE = 'NONE' as const;

export type PortfolioNode = {
  portfolioId: string;
  name: string;
  state: string;
  raw: Portfolio;
};

export type CampaignNode = {
  campaignKey: string;
  campaignId: string;
  adProduct: CampaignAdProduct;
  name: string;
  state: string;
  portfolioId: string | null;
  raw: SpCampaignNormalized;
};

export type AdGroupNode = {
  adGroupId: string;
  campaignId: string;
  adProduct: CampaignAdProduct;
  name: string;
  state: string;
  servingStatus: string;
  raw: Record<string, unknown>;
};

export type KeywordPin = {
  keywordId: string;
  adProduct: 'SP' | 'SB';
  campaignId: string;
  adGroupId: string;
};

export type KeywordDisplayRow = UnifiedKeywordRow;

/** Shared Workspace cross-filter: entity pins + attribute / report / product facets. */
export type EntityCrossFilter = {
  portfolioIds: string[];
  campaignKeys: string[];
  adGroupIds: string[];
  keywordIds: string[];
  /** SP | SB | SD (campaign ad product short labels). */
  adProducts: string[];
  /** Targeting type breakdown ids (keyword, auto, …). */
  targetingTypes: string[];
  /** Match type breakdown ids (exact, broad, …). */
  matchTypes: string[];
  /** Placement breakdown ids. */
  placements: string[];
  /** Audience segment ids (e.g. new_to_brand) — facet only until NTB ingest. */
  audienceSegments: string[];
  /** SearchTermEnrichedRow.rowKey values. */
  searchTermKeys: string[];
  /** Advertised-product ASINs. */
  asins: string[];
  /** AdProductRow.rowKey values (`SP|adId`). */
  adProductRowKeys: string[];
};

export type EntityGraphIndices = {
  campaignsByKey: Map<string, CampaignNode>;
  campaignsByPortfolioId: Map<string, CampaignNode[]>;
  adGroupsById: Map<string, AdGroupNode>;
  adGroupsByCampaignKey: Map<string, AdGroupNode[]>;
  keywordPinsByGroupKey: Map<string, KeywordPin[]>;
  keywordIdsByGroupKey: Map<string, string[]>;
  portfolioIdByCampaignKey: Map<string, string>;
  allKeywordIds: Set<string>;
};

export type EntityGraph = {
  portfolios: PortfolioNode[];
  campaigns: CampaignNode[];
  adGroups: AdGroupNode[];
  keywords: KeywordDisplayRow[];
  indices: EntityGraphIndices;
};

export type PropagatedConstraints = {
  portfolioIds: Set<string>;
  campaignKeys: Set<string>;
  adGroupIds: Set<string>;
  keywordIds: Set<string>;
  conflict: boolean;
};

export type FilteredEntities = {
  portfolios: PortfolioNode[];
  campaigns: CampaignNode[];
  adGroups: AdGroupNode[];
  keywords: KeywordDisplayRow[];
  conflict: boolean;
};

/** Optional data used to resolve attribute / report / product pins to campaign keys. */
export type WorkspaceResolutionContext = {
  /** Campaign keys that contributed to each targeting-type bucket. */
  campaignKeysByTargetingType?: Map<string, Set<string>>;
  /** Campaign keys that contributed to each match-type bucket. */
  campaignKeysByMatchType?: Map<string, Set<string>>;
  /** Campaign keys (graph keys) with spend/rows for each placement. */
  campaignKeysByPlacement?: Map<string, Set<string>>;
  /** Product-ad catalog for ASIN → campaigns. */
  productAdCatalog?: Array<{
    asin: string;
    campaignId: string;
    adProduct: string;
  }>;
  /** Search term rows keyed by rowKey. */
  searchTermsByKey?: Map<
    string,
    {
      campaignId: string;
      adGroupId: string;
      keywordId?: string;
      adProduct: string;
    }
  >;
  /** Ad product rows keyed by rowKey. */
  adProductRowsByKey?: Map<
    string,
    {
      campaignId: string;
      asin: string;
      adProduct: string;
    }
  >;
};
